import type { SiteConfig } from '../anonymization/engine'
import type { Schemas } from './api.gen'

export type SiteRole = 'site_admin' | 'coordinator' | 'member'
export const SITE_ROLES: readonly SiteRole[] = ['member', 'coordinator', 'site_admin']
/** Site admins and coordinators organize the site's work (ROLE-09). */
export const organizes = (membership: { role: SiteRole }) => membership.role === 'site_admin' || membership.role === 'coordinator'

export interface UserSummary {
  id: string
  username: string
  display_name: string
  email: string | null
  /** The address of their photo (AUTH-13); none: their initials stand for them. */
  photo?: string | null
}

/** A person found by the user search: who they are, not how to reach them (security audit M2). */
export interface UserMatch {
  id: string
  display_name: string
  /** Site codes, to tell two people of the same name apart. */
  sites: string[]
  /** Only for whoever typed it in full, and platform admins. */
  email: string | null
}

export interface SiteMembership {
  site_id: string
  site_code: string
  site_name: string
  role: SiteRole
}

export type AccountState = Schemas['AccountState']
/** An account in the platform admins' overview (ROLE-15); `last_login_at` null: never signed in. */
export type Account = Schemas['AccountOut']
/** How many accounts a search finds in each state, and how many no active site holds (ROLE-15). */
export type AccountsSummary = Schemas['AccountsSummary']
/** An active site by name, as every signed-in user may list them (`/sites/directory`). */
export type SiteName = Schemas['SiteName']

/** What a user tells the other users about themselves; an empty field shows nothing. */
export interface PublicProfile {
  honorific_prefix: string
  honorific_suffix: string
  position: string
  /** Institution and department. */
  affiliation: string
  /** The bare iD, such as 0000-0002-1825-0097. */
  orcid: string | null
  about: string
  /** Their name and place in the contributor rankings their sites' readers see (PRJ-25). */
  ranked: boolean
}

/** Who a user is beside their name (FOR-08): never their e-mail, specialty, experience or results (AUD-09). */
export interface Person {
  id: string
  name: string
  honorific_prefix: string
  honorific_suffix: string
  sites: string[]
  learner: boolean
  moderator: boolean
  /** The address of their photo (AUTH-13); none: their initials stand for them. */
  photo?: string | null
}

/** A user's profile as every signed-in user sees it; a deactivated user's shows only who they were. */
export interface Profile extends Person, Omit<PublicProfile, 'ranked'> {
  active: boolean
  /** Only its name stays (ROLE-14). */
  erased: boolean
  /** The viewer may deactivate and reactivate the account (ROLE-03). */
  may_manage: boolean
  /** The viewer may erase the deactivated account (ROLE-14). */
  may_erase: boolean
}

export interface Me extends UserSummary {
  is_platform_admin: boolean
  /** Maintains the label catalog (ROLE-10). */
  catalog_editor: boolean
  /** Moderates the forum (FOR-12). */
  forum_moderator: boolean
  specialty_id: number | null
  trauma_reading_since: string | null
  profile_complete: boolean
  public_profile: PublicProfile
  /** The installation's current terms of use are accepted (AUTH-10). */
  terms_accepted: boolean
  /** Password, second factor and sessions, in Keycloak (AUTH-02, AUTH-05). */
  account_url: string
  /** Keyboard shortcuts that differ from the defaults (ANN-05): action to chords. */
  shortcuts: Record<string, string[]> | null
  /** Notification kinds that stay in the platform only (NTF-02). */
  email_opt_out: string[]
  /** Kinds that come by e-mail only by choice, such as forum replies (FOR-10). */
  email_opt_in: string[]
  sites: SiteMembership[]
}

export interface Specialty {
  id: number
  name_en: string
  name_de: string
  active: boolean
}

export interface Site {
  id: string
  code: string
  name: string
  active: boolean
  /** The data processing agreement (AUD-08); uploads need a valid one. */
  dpa_version: string | null
  dpa_signed_on: string | null
  dpa_valid_until: string | null
  /** What the site allows for its data leaving it (SHR-03). */
  sharing_policy: SharingPolicy
  /** ISO 3166-1 alpha-2; grants to sites outside the EEA need a transfer basis (SHR-17). */
  country: string | null
  /** The site's time zone, set once (TOK-29). The site describes nothing about its patient IDs (TOK-32). */
  time_zone: string | null
  /** The ethics approval (ROLE-11) and how long the site keeps its data (ROLE-12); both only warn. */
  ethics_reference: string
  ethics_valid_until: string | null
  retention_until: string | null
  /** The end of the site's data (ROLE-12, ROLE-13): leaving from, deletion from, deleted at. */
  leaving_at: string | null
  data_deletion_on: string | null
  data_deleted_at: string | null
  /** When a site admin recorded that the site's token was destroyed (TOK-23); ends the retention reminders. */
  token_destroyed_at: string | null
}

export const SHARING_POLICIES = ['forbidden', 'approval', 'allowed'] as const
export type SharingPolicy = (typeof SHARING_POLICIES)[number]

export interface SiteMember {
  user: UserSummary
  role: SiteRole
  /** A deactivated account counts as no site admin (ROLE-01, ROLE-03). */
  active: boolean
}

export interface AnonymizationKey {
  id: string
  key_id: string
  label: string
  registered_at: string
  retired_at: string | null
  /** The key generator's number of the token for the site; null for keys registered before it (TOK-04). */
  serial: number | null
  origin: 'generated' | 'accepted' | null
  enrolled: number
}

export interface Enrollment {
  user: UserSummary
  enrolled_at: string
}

export interface EnrolledKey {
  key_id: string
  site_id: string
  site_name: string
  /** What new originals need besides the token: the time zone (TOK-29); none until a site admin sets it. */
  site_config: SiteConfig | null
}

/** The site's own identity provider (AUTH-11). */
export interface SsoConfig {
  display_name: string
  issuer: string | null
  client_id: string
  redirect_uri: string
}

/** A platform admin's read-only access to the site's studies (ROLE-08). */
export interface SupportGrant {
  id: string
  user: UserSummary
  granted_at: string
  expires_at: string
  revoked_at: string | null
}

export interface Named {
  id: string
  name: string
}

/** An active site, to grant or share with (SHR-12). */
export interface SiteDirectoryEntry extends Named {
  in_eea: boolean
}

/** `export` is granted for frozen collections only: to view and export them (SHR-12, PRJ-13). */
export const GRANT_ACCESS = ['view', 'annotate', 'export'] as const
export type GrantAccess = (typeof GRANT_ACCESS)[number]

/** A collection a grant covers; a frozen one opens as itself, not its studies. */
export interface GrantCollection extends Named {
  kind: CollectionKind
}

/** A site's studies granted to another site or named users of it (SHR-12, D47). */
export interface Grant {
  id: string
  site: Named
  recipient_site: Named
  /** None: every member of the recipient site. */
  users: UserSummary[]
  /** The site collections it covers; none: all of the site's studies (SHR-12). */
  collections: GrantCollection[]
  access: GrantAccess
  terms: string
  agreement: string
  transfer_basis: string | null
  expires_at: string | null
  created_at: string
  revoked_at: string | null
  active: boolean
  /** By the user who asked (SHR-06). */
  accepted: boolean
}

export interface SiteGrants {
  given: Grant[]
  received: Grant[]
}

export type ShareState = 'pending_approval' | 'refused' | 'awaiting_acceptance' | 'active' | 'expired' | 'revoked'

/** The decision of a site whose data would leave it (SHR-03, BEN-02). */
export interface SiteApproval {
  site: Named
  state: 'pending' | 'approved' | 'refused'
}

export interface ImageInfo {
  id: string
  site_id: string
  filename: string
  patient_pseudonym: string
  study_uid: string
  acquired_at: string | null
  sex: string | null
  age_tenths: number | null
  body_part: string | null
  laterality: string | null
  view_position: string | null
  rows: number
  columns: number
  bits_stored: number
  saved_window: SavedWindow | null
}

/** Another study of the same patient, `days` after (negative: before) the current image (ANN-18). */
export interface RelatedStudy {
  study_uid: string
  acquired_at: string | null
  days: number | null
  images: ImageInfo[]
}

/** An optimized window/level saved for an image (ANN-03) and the window it was adjusted from. */
export interface SavedWindow {
  center: number
  width: number
  source_center: number
  source_width: number
}

/** The same image (identical decoded pixels) stored again, as a study of its site, that the user may open. */
export interface ImageDuplicate {
  image_id: string
  site_name: string
}

/**
 * An image whose OXF-RI fingerprint agrees closely (UPL-20): evidence, not a verdict. Coverage is the
 * share of the 1,024 tests valid in both images, agreement the share of those that agree.
 */
export interface SimilarImage extends ImageDuplicate {
  coverage: number
  agreement: number
  inverted: boolean
}

export interface ImageDetail extends ImageInfo {
  duplicates: ImageDuplicate[]
  similar: SimilarImage[]
}

/** An accepted file of an upload whose image resembles images the uploader may open (UPL-20). */
export interface SimilarFile {
  name: string
  image_id: string
  similar: SimilarImage[]
}

/**
 * A search result. `own` marks the user's library images, which only they delete or keep `private`
 * from their site.
 */
export interface FoundImage extends ImageInfo {
  own: boolean
  private: boolean
  /** False while its storage location does not answer or is declared lost (COL-08, STO-09). */
  available?: boolean
  /** How often the user checked its study (ANN-32, ANN-33); null where they may not annotate it. */
  checks?: number | null
}

export interface ImagePage {
  /** Null where the search was asked not to count (`count: false`). */
  total: number | null
  /** Studies among them, each counted once (COL-09). */
  studies: number | null
  items: FoundImage[]
}

/** Where a search looks besides its filters: one of the caller's collections, or none of them (COL-10). */
export interface ImageScope {
  collection_id?: string
  outside_collections?: boolean
  /** The dynamic collection the filters define: the studies of those inside it join them (COL-09). */
  with_nested?: string
  /** Within the collection: only its studies that meet its goal, or only the others (COL-11). */
  goal?: 'met' | 'unmet'
}

/**
 * Something an image has or lacks (COL-02): a tag or shape label (any label without `label`), a place
 * in an assignment, a finished read, a resolution, or words in the comments and reports of its study
 * (ANN-29). "anyone" counts everyone's in the studies the user manages, except on images the user
 * reads (RES-02).
 */
export type AttributeOp = 'lt' | 'le' | 'gt' | 'ge' | 'eq' | 'ne' | 'between' | 'in' | 'not_in' | 'prefix'

/** A value of a label's attribute, holding on the same shape or tag as the label (COL-02). */
export interface AttributeCondition {
  attribute: string
  op: AttributeOp
  value: number | string | number[] | string[]
}

export interface Condition {
  /** `text`: words in the comments and reports of the study (ANN-29); `checked`: the study's checks (ANN-32). */
  kind: 'label' | 'assigned' | 'finished' | 'resolved' | 'text' | 'checked'
  label: string | null
  /** Only with a label. */
  where?: AttributeCondition[]
  /** The words of a `text` condition. */
  value?: string
  by: 'me' | 'anyone'
  present: boolean
  /** A check condition's count: the user's checks, or the users who checked (COL-02); 1 by default. */
  at_least?: number
}

/**
 * The one selection of images (PRJ-05, COL-02, D57): a list allows any of its values, an
 * empty one all; every condition holds, and one alternative of each `any_of` group.
 */
export interface ImageFilters {
  site_ids: string[]
  upload_ids: string[]
  body_parts: string[]
  lateralities: string[]
  view_positions: string[]
  sexes: string[]
  min_age_years: number | null
  max_age_years: number | null
  conditions: Condition[]
  /** Alternatives name only what they filter; the rest is everything. */
  any_of?: Partial<ImageFilters>[][]
}

/** A saved selection of images (COL-01): it always holds the images that match now (COL-03). */
/** Dynamic: the studies its filters match now; manual: the studies put into it (COL-06). */
/** A frozen collection is fixed at one moment, with chosen annotations (PRJ-12). */
export type CollectionKind = 'dynamic' | 'manual' | 'frozen'

export interface Collection {
  id: string
  name: string
  kind: CollectionKind
  /** A manual collection's are empty. */
  filters: ImageFilters
  /** The collection it sits inside, which then covers its studies too (COL-09). */
  parent_id: string | null
  /** The site it belongs to; none: its creator's own (COL-04). */
  site_id: string | null
  /** The labeling profile its studies open with and its jobs start from (LBL-07). */
  labeling_profile_id: string | null
  /** The filters its studies are to meet, which its progress counts (COL-11); none: no goal. */
  goal?: ImageFilters | null
  /** The user may change it: their own, or one of a site whose work they organize. */
  editable: boolean
  created_at: string
  updated_at: string
}

export type FileState = 'pending' | 'uploaded' | 'validating' | 'accepted' | 'rejected' | 'duplicate'

export interface UploadFile {
  id: string
  name: string
  state: FileState
  reason: string | null
  image_id: string | null
  upload_url: string | null
}

export interface UploadSummary {
  id: string
  created_at: string
  counts: Partial<Record<FileState, number>>
  /** Annotation sets the files brought along, and those nobody was named for yet (UPL-09). */
  imports: number
  pending_imports: number
}

export interface UploadDetail extends UploadSummary {
  files: UploadFile[]
  /** How many files have their result; a poll asks for those after it (UPL-06). */
  results: number
}

export type ShapeType = 'rectangle' | 'polygon' | 'polyline' | 'points' | 'mask'
export type LabelType = ShapeType | 'tag'

export interface LabelAttribute {
  name: string
  input_type: 'radio' | 'select' | 'number' | 'checkbox' | 'text'
  /** Choices, or [minimum, maximum, step] for numbers. */
  values: string[]
  /** Null under a labeling profile that leaves the answer open (LBL-05). */
  default_value: string | null
}

export interface Label {
  name: string
  color: string
  type: LabelType
  /** The attributes offered, in order; under a labeling profile only those it asks for (LBL-05). */
  attributes: LabelAttribute[]
  /** Set by the active labeling profile: the label's other attributes, still answerable in its details. */
  more?: LabelAttribute[]
  /** Set by the active labeling profile: the attributes shown above and below a box, and those a job requires. */
  top?: string
  bottom?: string
  required?: string[]
  /** Set by the active labeling profile: the label's objects start locked (ANN-30) or hidden (ANN-12), or stay locked while it is active. */
  start_locked?: boolean
  always_locked?: boolean
  start_hidden?: boolean
  /** Offered for no new annotations; existing ones keep it (LBL-03). */
  hidden?: boolean
  /** A system tag the platform sets, such as `checked`; never a finding, never offered (LBL-09). */
  system?: boolean
  /** False: its boxes stay out of the gallery's box heatmap, as crop boxes do (IMG-09). */
  include_in_box_heatmap?: boolean
  /** True: its boxes can be rotated (ANN-01); never crop boxes, as the crop view cuts upright ones (ANN-31). */
  rotatable?: boolean
  /** An image tag that gives one answer for the whole study, kept equal on its views (LBL-10). */
  study?: boolean
  /** Codes from SNOMED CT or RadLex that name the label's finding (LBL-12). */
  codes?: LabelCode[]
  /** The catalog's default key for picking the label (LBL-02); users may change it (ANN-05). */
  shortcut?: string
  /** A free-text class a user named, never one of the catalog's (LBL-08). */
  free_text?: boolean
}

/** A label proposed to the catalog editors, who approve it, possibly adjusted, or decline it with a reason (LBL-02). */
export interface LabelProposal {
  id: string
  name: string
  type: LabelType
  explanation: string
  attributes: LabelAttribute[]
  status: 'pending' | 'approved' | 'declined'
  proposed_by: Person
  created_at: string
  decided_by: Person | null
  decided_at: string | null
  /** The name it joined the catalog under. */
  label: string | null
  /** Why it was declined. */
  reason: string | null
}

export interface LabelCode {
  system: 'SNOMED CT' | 'RadLex'
  code: string
  display: string
}

/**
 * CVAT-compatible, in image pixels; rectangles are [x1, y1, x2, y2], masks [x, y, width, height]
 * with run lengths `rle` inside that box. Times are set by the server.
 */
export interface Shape {
  id: string
  type: ShapeType
  label: string
  points: number[]
  rle?: number[]
  /** Rectangle rotation, clockwise degrees about its center; absent means zero. */
  rotation?: number
  attributes: Record<string, string>
  origin?: Origin
  created_at?: string | null
  updated_at?: string | null
}

/** An image tag; times are set by the server, by label (ANN-10). */
export interface Tag {
  label: string
  attributes: Record<string, string>
  origin?: Origin
  created_at?: string | null
  updated_at?: string | null
}

/** Where an accepted model suggestion came from (MOD-07, MOD-13). */
export interface Origin {
  prediction_id: string
  model_id: string
  model: string
  version: string
  confidence: number
}

/**
 * A comment in the user's own annotation set (ANN-25): at a point in image pixels, or on the whole
 * image without one. Times are set by the server.
 */
export interface Comment {
  id: string
  x: number | null
  y: number | null
  text: string
  /** The speech model that dictated it, if it was (ANN-22). */
  dictated?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface AnnotationData {
  shapes: Shape[]
  tags: Tag[]
  /** Missing in sets saved before comments existed. */
  comments?: Comment[]
}

export interface AnnotationSet {
  revision: number
  data: AnnotationData
  /** The author named in the uploaded file the set came with (UPL-09). */
  imported_from?: string | null
}

/**
 * One image's change of an image tag in the user's own set (`POST /annotations/tags`, IMG-03): its
 * `attributes` over the tag's own or the label's defaults, as sent with `exact`, or null to clear it;
 * only where it has the values `only_with`, and only on `revision` where given (ANN-08).
 */
export interface TagChange {
  image_id: string
  attributes?: Record<string, string> | null
  exact?: boolean
  only_with?: Record<string, string>
  revision?: number
}

/** What became of a tag change, with the set's revision and the tag's attributes before (null: not set). */
export interface TagOutcome {
  image_id: string
  status: 'changed' | 'unchanged' | 'conflict' | 'refused'
  revision: number
  before: Record<string, string> | null
}

export type AssignmentState = 'new' | 'in_progress' | 'finished' | 'returned'
export type ReadingState = 'open' | 'in_progress' | 'finished'

/** When readers' findings count as the same finding and when they agree (RES-03). */
export interface ComparisonSettings {
  match_iou: number
  agree_iou: number
  match_distance: number
  agree_distance: number
  /** Null: numbers agree within one step of the attribute (RES-03). */
  numeric_tolerance: number | null
}

/** What an annotation job sets for its readers (PRJ-19, PRJ-20). */
export interface JobSettings {
  /** The catalog labels the job offers; null: all of them. */
  labels: string[] | null
  /** Readers may name free-text classes as well (LBL-08). */
  free_text: boolean
  /** Readers see no other set on the job's images until they finished their job (RES-02, D46). */
  blinded: boolean
  /** With blinded: readers see the indication and clinical question of clinical reports (RES-18). */
  clinical_context: boolean
  ai_suggestions: boolean
  /** With ai_suggestions: shown as soon as an image opens instead of on request (RES-15). */
  ai_at_once: boolean
  prior_studies: boolean
  instructions: string
}

/** Someone who may work in the site's jobs: a member of the site, or a recipient of its grants. */
export interface CandidateReader {
  user: UserSummary
  role: SiteRole | 'grantee'
}

export interface AnnotationJob extends JobSettings {
  id: string
  /** Whose work the job is (D41). */
  site_id: string
  name: string
  readers_per_image: number
  comparison: ComparisonSettings
  adjudicator_id: string | null
  /** Who checks its completed assignments (PRJ-08). */
  reviewer_ids: string[]
  created_at: string
  /** Read-only since (PRJ-24). */
  closed_at: string | null
  images: number
  assignments: number
  finished_assignments: number
  /** Null: Standard (LBL-07). */
  labeling_profile: PinnedProfile | null
}

export interface AssignmentSummary {
  id: string
  job_id: string
  job_name: string
  /** Whose work the job is (D41). */
  site_id: string
  site_name: string
  state: AssignmentState
  /** The job is read-only since (PRJ-24). */
  closed_at: string | null
  images: number
  finished_images: number
}

/** An image in the reading workspace, with its reading state where it is read in a job (RES-16). */
export interface WorkspaceItem {
  image: ImageInfo
  state?: ReadingState
}

export interface AssignmentItem extends WorkspaceItem {
  state: ReadingState
}

export interface AssignmentDetail extends AssignmentSummary, JobSettings {
  items: AssignmentItem[]
  /** What the job asks for; null: Standard (LBL-07). */
  labeling_profile: PinnedProfile | null
}

/** Context for opening any accessible image in the annotation workspace. */
export interface ImageWorkspace {
  images: ImageInfo[]
  site_name: string
  permissions: string[]
  /** The image's saved window is everyone's, so not every annotator saves it (D36). */
  saves_window: boolean
  /** False while the user's job holds the image without AI suggestions (RES-15). */
  ai_suggestions: boolean
  /** False while the user's job keeps them on request (RES-15). */
  ai_at_once: boolean
}

/** A finished or returned job for its reviewers (PRJ-08). */
export interface ReviewAssignment extends AssignmentSummary {
  reader: string
}

export interface ReviewDetail extends AssignmentDetail {
  reader: string
}

/** A site's work at a glance for its coordinators (PRJ-09). */
export interface Dashboard {
  sites: { site: string; studies: number }[]
  jobs: { id: string; name: string; assignments: Partial<Record<AssignmentState, number>> }[]
  readers: { reader: string; assigned: number; finished: number; seconds: number }[]
  labels: { label: string; kind: 'shape' | 'tag'; count: number }[]
}

export const NOTIFICATION_KINDS = [
  'job_assigned',
  'job_returned',
  'adjudication_assigned',
  'upload_finished',
  'ethics_expiring',
  'ethics_ended',
  'retention_ending',
  'grant_received',
  'benchmark_approval',
  'benchmark_invited',
  'site_departure',
  'teaching_approval',
  'model_released',
  'model_retired',
  'forum_removed',
  'label_proposal_decided',
  'site_role',
  'site_role_ended',
] as const
/** Platform alerts (OPS-06) go to platform admins only. */
export const ALERT_KINDS = ['ops_alert', 'ops_resolved'] as const
/** Come by e-mail only when the user asks for it (FOR-10). */
export const OPT_IN_KINDS = ['forum_reply'] as const
/** Flags reach the forum's moderators and platform admins (FOR-11). */
export const MODERATOR_KINDS = ['forum_flag'] as const
/** New label proposals reach the catalog editors (LBL-02). */
export const EDITOR_KINDS = ['label_proposed'] as const
/** Security notices always come by e-mail too (AUTH-07). */
export type SecurityKind = 'account_locked'

/** Fails the type check while the API sends a notification kind the lists above leave out, or the
 * other way round, so a kind added on one side cannot be missed on the other (NTF-01). */
type Nothing<T extends never> = T
export type UnlistedNotificationKinds = Nothing<
  Exclude<Schemas['NotificationKind'], NotificationItem['kind']> | Exclude<NotificationItem['kind'], Schemas['NotificationKind']>
>

/** A notification in the platform (NTF-01). */
export interface NotificationItem {
  id: string
  kind:
    | (typeof NOTIFICATION_KINDS)[number]
    | (typeof ALERT_KINDS)[number]
    | (typeof OPT_IN_KINDS)[number]
    | (typeof MODERATOR_KINDS)[number]
    | (typeof EDITOR_KINDS)[number]
    | SecurityKind
  payload: Record<string, string | number>
  link: string
  created_at: string
  read_at: string | null
}

export interface Inbox {
  unread: number
  items: NotificationItem[]
}

/** A site's own bucket (STO-03); the credentials are never returned (STO-05). */
export interface StorageLocation {
  id: string
  name: string
  /** A linked bucket's; null behind a site connector (STO-11). */
  endpoint: string | null
  region: string | null
  bucket: string | null
  prefix: string | null
  connector_id: string | null
  /** A Nextcloud's address (STO-20). */
  address: string | null
  /** A cloud drive's service, and a Google Drive's or a Nextcloud's folder (STO-28, STO-29, STO-20). */
  provider: CloudProvider | null
  folder: string | null
  created_at: string
  credentials_updated_at: string
  active_since: string | null
  takes_uploads: boolean
  /** Declared lost by a site admin (STO-16): its images open as lost until uploaded again. */
  lost_at: string | null
  /** The site lets the platform render, fingerprint and run models on its images here (STO-14). */
  processing_allowed: boolean
}

export const STORAGE_STEPS = ['write', 'read', 'list', 'private', 'delete'] as const

/** The platform's check of a bucket (STO-04). */
export type StorageCheck = Record<(typeof STORAGE_STEPS)[number], boolean> & { error: string | null }

/** URLs for the browser's part of the check. */
export interface BrowserCheck {
  key: string
  put_url: string
  get_url: string
}

export interface LocationChecked {
  /** Files of the location's images that a copy of its bucket lacks (STO-15). */
  missing: number
  check: StorageCheck
  location: StorageLocation | null
  browser_check: BrowserCheck | null
}

/** A site's storage locations and what it keeps where (STO-10). */
export type CloudProvider = 'gdrive' | 'dropbox' | 'nextcloud'

export interface SiteStorageInfo {
  linked_buckets: boolean
  /** The cloud drives the platform has an app for, and the Nextcloud, which needs none (STO-28, STO-29, STO-20). */
  clouds: CloudProvider[]
  cors: Record<string, unknown>[]
  locations: StorageLocation[]
  /** The site's images per location (STO-10); a null location is hosted storage. */
  use: { location_id: string | null; images: number; bytes: number }[]
  /** The site's images in each of its collections (STO-10). */
  collections: { collection_id: string; name: string; images: number; bytes: number }[]
}

/** A move of the site's images to another of its locations, run by the worker image by image (STO-12). */
export interface StorageMove {
  id: string
  /** Null: hosted storage. */
  target_location_id: string | null
  /** The collection whose images it moves, those it held at the start; null: all of the site's images. */
  collection_id: string | null
  /** The collection's name, where the user sees the collection. */
  collection: string | null
  state: 'pending' | 'running' | 'done' | 'failed' | 'cancelled'
  total: number
  done: number
  error: string | null
  created_at: string
  finished_at: string | null
}

/** The platform's status for platform admins (OPS-05, OPS-07). */
export interface PlatformStatus {
  services: { name: string; ok: boolean; detail: Record<string, unknown> | null }[]
  queues: Record<string, number>
  /** Each site's use of the inference service today and in the last 30 days (INF-13). */
  inference: { site_id: string; site: string; today: number; images: number; rejected: number; errors: number }[]
  storage: { images: number; upload_bytes: number; export_bytes: number }
  disks: { path: string; total: number; free: number }[]
  backup: { last_attempt?: string; last_success: string | null; error: string | null } | null
  restore_check: { checked_at: string; backup: string; images: number } | null
  /** When the backup service last copied the restore ledger off-site (SHR-16). */
  ledger_copy: { last_success: string } | null
}

/** An audit log entry (AUD-01 to AUD-03). */
export interface AuditEntry {
  id: number
  occurred_at: string
  actor: string | null
  action: string
  target_type: string
  target_id: string
  site_id: string | null
  /** Only on entries from before projects went; kept for the hash chain. */
  project_id: string | null
  details: Record<string, unknown>
  hash: string
}

/** One save of an annotation set and the labels it changed (ANN-10). */
export interface HistoryEvent {
  user: string
  own: boolean
  revision: number
  changes: Partial<Record<'added' | 'changed' | 'removed' | 'tagged' | 'untagged' | 'commented' | 'comment_changed' | 'uncommented', string[]>>
  created_at: string
}

/** A reviewer's comment pinned to a point of an image, in image pixels (ANN-11). */
export interface ReviewComment {
  id: string
  image_id: string
  x: number
  y: number
  text: string
  author_id: string
  author: string
  created_at: string
}

export interface Reader {
  id: string
  display_name: string
  specialty_id: number | null
  experience_years: number | null
}

export type Reason = 'missed' | 'label' | 'location' | 'attribute'

export interface Cluster {
  index: number
  kind: 'shape' | 'tag'
  type: string
  label: string
  agreed: boolean
  reasons: Reason[]
  members: { reader_id: string; shape: Shape | null; tag: Tag | null }[]
  missing: string[]
}

export type FindingStatus = 'agreed' | 'resolved' | 'disputed'

export interface ResolvedFinding {
  kind: 'shape' | 'tag'
  label: string
  status: FindingStatus
  present: boolean | null
  shape: Shape | null
  tag: Tag | null
  support: number
  readers: number
  resolved_by: string
  sources: { reader_id: string; shape_id: string | null }[]
  reason: string | null
  evidence: { study_uid: string; days: number } | null
}

export interface Resolution {
  method: 'majority' | 'adjudication'
  outdated: boolean
  updated_at: string
  data: { findings: ResolvedFinding[] }
}

export interface ImageComparison {
  readers: Reader[]
  /** Reader id: the author named in the uploaded file their set came with (UPL-09). */
  imported: Record<string, string>
  /** Readers who had annotated the image before their job (PRJ-23). */
  earlier: string[]
  clusters: Cluster[]
  resolution: Resolution | null
  version: string
}

export interface JobImage {
  image: ImageInfo
  readers: number
  finished: number
  agreed: number
  discrepant: number
  resolution: 'majority' | 'adjudication' | 'outdated' | null
  disputed: number
}

export interface PairAgreement {
  reader_a: string
  reader_b: string
  images: number
  count_a: number
  count_b: number
  matched: number
  f1: number | null
  iou: number | null
  dice: number | null
  kappa: number | null
  positive_agreement: number | null
  negative_agreement: number | null
  /** AO classifications (RES-04): among the `classified` studies either reader classified, the share on which both name the same codes at each level. */
  levels?: Record<AoLevel, number> | null
  classified?: number | null
}

/** The levels of an AO code agreement compares: bone and segment, E/M/D, child code, severity. */
export const AO_LEVELS = ['segment', 'zone', 'pattern', 'severity'] as const
export type AoLevel = (typeof AO_LEVELS)[number]

export interface LabelAgreement {
  label: string
  type: string
  /** A study tag (LBL-10), whose `images` count studies. */
  study?: boolean
  pairs: PairAgreement[]
}

export interface Agreement {
  readers: Reader[]
  labels: LabelAgreement[]
}

export interface MajorityResult {
  resolved_images: number
  disputed_findings: number
  skipped_images: number
}

/** An adjudicator's decision on one discrepant finding (RES-07). */
export interface Decision {
  cluster: number
  action: 'accept' | 'merge' | 'dispute'
  reader_id?: string
  label?: string
  reason?: string
  /** A later study of the same patient that supports the decision (RES-13). */
  evidence_study_uid?: string
}

export interface AdjudicationJob {
  job_id: string
  job_name: string
  site_name: string
  adjudicated_images: number
}

/** A job's resolution, the readers' sets as they stand or their first readings, or the resolution with one of these (RES-11). */
/** RES-11, or `none`: the studies alone (COL-06). */
/** A job's annotations to freeze, or without a job the sets of those who checked each study (RES-11), or none. */
export type AnnotationChoice = 'resolution' | 'readers' | 'both' | 'first_readings' | 'resolution_first_readings' | 'checked' | 'none'
export const WITH_RESOLUTION: ReadonlySet<AnnotationChoice> = new Set(['resolution', 'both', 'resolution_first_readings'])
export const WITH_READERS: ReadonlySet<AnnotationChoice> = new Set(['readers', 'both', 'first_readings', 'resolution_first_readings'])
export type DisputedTreatment = 'omit' | 'include' | 'ignore' | 'share'
export type ExportFormat = 'cvat' | 'png_cvat' | 'coco' | 'yolo' | 'dicom'
export const EXPORT_FORMATS: readonly ExportFormat[] = ['coco', 'yolo', 'png_cvat', 'cvat', 'dicom']

export interface FrozenCollectionVersion {
  id: string
  /** Whose work it is (D41). */
  site_id: string
  /** None once its job was deleted (PRJ-24). */
  job_id: string | null
  name: string
  settings: {
    annotations: AnnotationChoice
    disputed: DisputedTreatment
  }
  summary: {
    images: number
    patients: number
    splits: Record<'train' | 'validation' | 'test', number>
    /** Patients connected by equal pixels whose bins fell into different sets (PRJ-11); absent in older versions. */
    connected?: number
    excluded: Partial<Record<'outdated' | 'unresolved' | 'unread', number>>
  }
  sha256: string
  created_at: string
  /** Connected patients turned out to sit in different sets of it (PRJ-11). */
  split_review_at: string | null
}

/** A frozen collection as the viewer may use it: as its coordinator or through a grant (`via_grant`). */
export interface FrozenCollectionView extends FrozenCollectionVersion {
  site_name: string
  can_export: boolean
  via_grant: boolean
  /** Evaluate model runs against it (MOD-15). */
  can_evaluate: boolean
  /** Coordinate benchmarks of it (BEN-01). */
  can_benchmark: boolean
}

/** An image of a frozen collection with its annotation sets: "resolution" and reader IDs. */
export interface FrozenImage {
  id: string
  split: 'train' | 'validation' | 'test'
  patient: string
  study_uid: string
  width: number
  height: number
  sex: string | null
  age_years: number | null
  body_part: string | null
  laterality: string | null
  view_position: string | null
  sets: Record<string, AnnotationData>
  /** False while its storage location does not answer or is declared lost (STO-09); not frozen. */
  available?: boolean
}

/** The frozen content of a frozen collection, without storage details. */
export interface FrozenDocument {
  name: string
  /** None when frozen from a collection without a job (COL-06). */
  task: { id: string; name: string } | null
  /** The labeling profile revision its annotations were compared under (PRJ-12); revision null: Standard. Absent before it was recorded, null without annotations. */
  labeling_profile?: { name: string; revision: number | null } | null
  labels: Label[]
  images: FrozenImage[]
}

export interface FrozenExport {
  id: string
  frozen_collection_id: string
  format: ExportFormat
  state: 'pending' | 'running' | 'done' | 'failed'
  error: string | null
  size: number | null
  files: number | null
  sha256: string | null
  /** Requested through a share: readers under codes (RES-14). */
  readers_coded: boolean
  created_at: string
  finished_at: string | null
}

export type ModelKind = 'classification' | 'detection' | 'segmentation' | 'interactive'
export const MODEL_KINDS: readonly ModelKind[] = ['detection', 'segmentation', 'classification', 'interactive']
/** The label types each kind of model output may map to (as in inference/router.py). */
export const MODEL_TARGETS: Record<ModelKind, readonly LabelType[]> = {
  detection: ['rectangle'],
  segmentation: ['polygon', 'mask'],
  classification: ['tag'],
  interactive: ['polygon', 'mask'],
}

export interface RenderProfile {
  window: 'dicom' | 'percentile' | 'minmax'
  low: number
  high: number
  invert_monochrome1: boolean
  max_size: number | null
}

export interface ModelVersion {
  id: string
  name: string
  version: string
  kind: ModelKind
  service_url: string
  key: string
  classes: string[]
  rendering: RenderProfile
  intended_use: string
  license: string
  /** Of the weights, when registered with them; answers from other weights are refused. */
  sha256: string | null
  active: boolean
  created_at: string
  /** Its classes mapped to catalog labels once a platform admin enables it for annotation (MOD-04). */
  class_map: Record<string, string> | null
  /** Runs in the background on images as they arrive (MOD-17). */
  automatic: boolean
  /** Its suggestions appear as soon as an image opens, where no job keeps them on request (RES-15). */
  shown_at_once: boolean
}

/** The model versions enabled for annotation (MOD-04, MOD-05). */
export const forAnnotation = (models: readonly ModelVersion[] | undefined): ModelVersion[] =>
  models?.filter((model) => model.class_map !== null) ?? []

export type RunState = 'pending' | 'running' | 'done' | 'failed' | 'cancelled'

/** A background model run (MOD-09): over a task's images, or without one over the images its filters chose (COL-05). */
export interface ModelRun {
  id: string
  job_id: string | null
  filters: ImageFilters | null
  model_id: string
  model: string
  version: string
  confidence: number
  state: RunState
  total: number
  done: number
  error: string | null
  created_at: string
  finished_at: string | null
}


export interface Suggestion {
  kind: 'shape' | 'tag'
  type: ShapeType | null
  label: string
  points: number[]
  rle: number[] | null
  origin: Origin
}

export interface PredictionResult {
  /** Null when only stored results were asked for and none is there yet. */
  prediction_id: string | null
  suggestions: Suggestion[]
}

/** A site's access to the inference service for partner sites (INF-02, INF-14, INF-15). */
export interface InferenceAccess {
  until: string
  daily_limit: number | null
  active: boolean
  terms_accepted: boolean
}

/** A key of one of the site's systems (INF-03, INF-05); its secret is shown once, on creation. */
export interface InferenceKey {
  id: string
  name: string
  networks: string[]
  expires_on: string
  created_at: string
  revoked_at: string | null
  last_used_at: string | null
}

export interface InferenceUse {
  day: string
  model: string | null
  images: number
  rejected: number
  errors: number
}

export interface SiteInference {
  access: InferenceAccess | null
  keys: InferenceKey[]
  use: InferenceUse[]
  accepted_images: number
  finished_readings: number
}

/** A model version released for the inference service, with its model card (INF-06, INF-07). */
export interface ReleasedModel {
  name: string
  version: string
  kind: ModelKind
  classes: string[]
  card: string
  released_on: string
  available_until: string | null
}

export interface Classification {
  positives: number
  negatives: number
  sensitivity: number | null
  sensitivity_ci: [number, number] | null
  specificity: number | null
  specificity_ci: [number, number] | null
  auc: number | null
}

export interface Detection {
  findings: number
  predictions: number
  precision: number | null
  recall: number | null
  ap: number | null
  froc: { false_positives_per_image: number; sensitivity: number }[]
}

/** A benchmark keeps only the metrics it chose (BEN-01), so either kind may be missing. */
export interface Metrics {
  images: number
  map: number | null
  labels: Record<string, { classification?: Classification; detection?: Detection }>
}

export const DIMENSIONS = ['age_group', 'sex', 'body_part', 'site'] as const
export type Dimension = (typeof DIMENSIONS)[number]

/** Scores against one reference: the resolution or one reader (MOD-15, BEN-06). */
export interface ScoredResult {
  reference: string
  overall: Metrics
  breakdown: Record<Dimension, Record<string, Metrics>>
}

export type BenchmarkMetric = 'classification' | 'detection'
export const BENCHMARK_METRICS: readonly BenchmarkMetric[] = ['classification', 'detection']
/** How a submission appears on the leaderboard (BEN-07). */
export type Shown = 'hidden' | 'named' | 'anonymous'
export const SHOWN: readonly Shown[] = ['hidden', 'named', 'anonymous']

/** A frozen collection's test images against a hidden reference (BEN-01). */
export interface Benchmark {
  id: string
  frozen_collection_id: string
  name: string
  reference: 'resolution' | 'readers'
  labels: string[]
  metrics: BenchmarkMetric[]
  threshold: number
  weekly_submissions: number
  terms: string
  cases: number
  created_at: string
  /** False once its frozen collection was withdrawn as a whole (AUD-06). */
  available: boolean
  /** Its frozen collection put connected patients into different sets: results to review (PRJ-11). */
  split_review: boolean
}

/** A benchmark as the user meets it: as coordinator, or invited with a state (BEN-02). */
export interface MyBenchmark extends Benchmark {
  coordinator: boolean
  state: ShareState | null
  participant_id: string | null
  /** Submissions left in the last seven days, for an active participant (BEN-05). */
  remaining: number | null
}

export interface BenchmarkParticipant {
  id: string
  user: UserSummary | null
  site: Named | null
  invited_at: string
  state: ShareState
  approvals: SiteApproval[]
  submissions: number
}

export interface BenchmarkSubmission {
  id: string
  model_name: string
  model_version: string
  description: string
  shown: Shown
  created_at: string
  results: ScoredResult[]
}

export interface LeaderboardEntry {
  id: string
  model_name: string
  model_version: string
  description: string
  /** Null: anonymous, or one of the platform's released versions. */
  author: string | null
  platform: boolean
  mine: boolean
  created_at: string
  map: number | null
  auc: number | null
}

/** A participant waiting for the site's decision on its images (BEN-02, SHR-03). */
export interface BenchmarkApproval {
  participant_id: string
  benchmark: Named
  frozen_collection: Named
  user: UserSummary | null
  site: Named | null
  cases: number
}

/** A labeling profile's choice for one label (LBL-05). */
export interface LabelBinding {
  attributes: { name: string; required: boolean; default_value: string | null }[]
  top: string | null
  bottom: string | null
  /** The label's objects start locked (ANN-30) or hidden (ANN-12) in the workspace, or stay locked while the profile is active. */
  start_locked?: boolean
  always_locked?: boolean
  start_hidden?: boolean
}

export type Bindings = Record<string, LabelBinding>

export type ProfileScope = 'personal' | 'site' | 'platform'

/** A profile revision; a job pins one for good (LBL-07). */
export interface PinnedProfile {
  revision_id: string
  profile_id: string
  name: string
  revision: number
  bindings: Bindings
  /** The labels in the order the workspace offers them; the others follow in the catalog's order (LBL-05). */
  label_order: string[]
}

export interface LabelingProfile extends PinnedProfile {
  scope: ProfileScope
  site_id: string | null
  owner_name: string
  editable: boolean
}

export interface LabelingProfiles {
  profiles: LabelingProfile[]
  /** The profile the workspace starts with: the collection's default, else the user's; null: Standard. */
  default_id: string | null
}

/** A patient whose images disagree (TOK-26), for review. */
export interface PatientWarning {
  /** The patient's pseudonyms: one, or several associated (TOK-25). */
  patients: string[]
  images: string[]
  sexes: string[]
  /** Split bins from different birth dates. */
  birth_dates: number
}

/** Two pseudonyms of one patient (TOK-25). */
export interface PatientAssociation {
  id: string
  first: string
  second: string
  created_at: string
}
